import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Pill,
  Plus,
  Trash2,
  BookmarkPlus,
  Search,
  Check,
  Sparkles,
} from 'lucide-react';
import { prescriptionTemplateService } from '@/services/prescriptionTemplateService';
import { toast } from 'sonner';

export const PrescriptionTemplateModal = ({
  open,
  onOpenChange,
  onApplyTemplate,
  initialMedicines = [],
  clinicId,
  doctorId,
}) => {
  const [activeTab, setActiveTab] = useState(initialMedicines.length > 0 ? 'create' : 'browse');
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');

  // Create form state
  const [templateName, setTemplateName] = useState('');
  const [templateCategory, setTemplateCategory] = useState('General OPD');
  const [templateDescription, setTemplateDescription] = useState('');
  const [items, setItems] = useState(
    initialMedicines.length > 0
      ? initialMedicines.map((m, idx) => ({
          medicineName: m.medicineName || m.medication || '',
          amount: m.amount || m.dosage || '1 tablet',
          quantity: Number(m.quantity) || 10,
          frequency: m.frequency || 'Twice daily (BD)',
          duration: m.duration || '5 days',
          notes: m.notes || m.instructions || '',
          displayOrder: idx + 1,
        }))
      : [
          {
            medicineName: '',
            amount: '1 tablet',
            quantity: 10,
            frequency: 'Twice daily (BD)',
            duration: '5 days',
            notes: 'After food',
            displayOrder: 1,
          },
        ]
  );

  React.useEffect(() => {
    if (open) {
      loadTemplates();
      if (initialMedicines.length > 0) {
        setActiveTab('create');
        setItems(
          initialMedicines.map((m, idx) => ({
            medicineName: m.medicineName || m.medication || '',
            amount: m.amount || m.dosage || '1 tablet',
            quantity: Number(m.quantity) || 10,
            frequency: m.frequency || 'Twice daily (BD)',
            duration: m.duration || '5 days',
            notes: m.notes || m.instructions || '',
            displayOrder: idx + 1,
          }))
        );
      } else {
        setActiveTab('browse');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const res = await prescriptionTemplateService.getAll({ clinicId });
      setTemplates(res.data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleAddItem = () => {
    setItems((prev) => [
      ...prev,
      {
        medicineName: '',
        amount: '1 tablet',
        quantity: 10,
        frequency: 'Twice daily (BD)',
        duration: '5 days',
        notes: 'After food',
        displayOrder: prev.length + 1,
      },
    ]);
  };

  const handleRemoveItem = (index) => {
    setItems((prev) => prev.filter((_, i) => i !== index));
  };

  const handleItemChange = (index, field, value) => {
    setItems((prev) => {
      const copy = [...prev];
      copy[index] = { ...copy[index], [field]: value };
      return copy;
    });
  };

  const handleSaveTemplate = async () => {
    if (!templateName.trim()) {
      toast.error('Please enter a template name');
      return;
    }
    const validItems = items.filter((it) => it.medicineName.trim().length > 0);
    if (validItems.length === 0) {
      toast.error('Please add at least one medicine with a valid name');
      return;
    }

    try {
      await prescriptionTemplateService.create({
        name: templateName.trim(),
        category: templateCategory,
        description: templateDescription,
        clinicId,
        doctorId,
        items: validItems,
      });
      toast.success(`Template "${templateName}" saved successfully!`);
      loadTemplates();
      setActiveTab('browse');
    } catch (e) {
      toast.error('Failed to save template');
    }
  };

  const handleApply = (template) => {
    if (!template || !template.items) return;
    // Explicit COPY of values into prescription (immutability rule)
    const clonedItems = template.items.map((it) => ({
      medication: it.medicineName,
      dosage: it.amount,
      frequency: it.frequency,
      duration: it.duration,
      instructions: it.notes,
      amount: it.amount,
      quantity: it.quantity,
      notes: it.notes,
    }));
    onApplyTemplate(clonedItems, template.name);
    toast.success(`Applied template "${template.name}"`);
    onOpenChange(false);
  };

  const filteredTemplates = templates.filter((t) => {
    const q = search.toLowerCase();
    return (
      t.name.toLowerCase().includes(q) ||
      (t.category && t.category.toLowerCase().includes(q)) ||
      (t.items && t.items.some((it) => it.medicineName.toLowerCase().includes(q)))
    );
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col p-0 overflow-hidden">
        <DialogHeader className="p-5 bg-slate-900 text-white flex-shrink-0">
          <div className="flex items-center justify-between">
            <div>
              <DialogTitle className="text-base font-bold text-white flex items-center gap-2">
                <BookmarkPlus className="h-5 w-5 text-teal-400" />
                Prescription Templates
              </DialogTitle>
              <DialogDescription className="text-xs text-slate-300">
                Reusable prescription protocols for quick clinical authoring
              </DialogDescription>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant={activeTab === 'browse' ? 'secondary' : 'ghost'}
                size="sm"
                onClick={() => setActiveTab('browse')}
                className="text-xs text-slate-200"
              >
                Browse ({templates.length})
              </Button>
              <Button
                variant={activeTab === 'create' ? 'secondary' : 'ghost'}
                size="sm"
                onClick={() => setActiveTab('create')}
                className="text-xs text-slate-200"
              >
                + New Template
              </Button>
            </div>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {activeTab === 'browse' ? (
            <div className="space-y-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Search templates by disease, category, or medication..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9 text-xs"
                />
              </div>

              {loading ? (
                <div className="p-8 text-center text-xs text-slate-500">Loading templates...</div>
              ) : filteredTemplates.length === 0 ? (
                <div className="p-8 text-center bg-slate-50 rounded-xl border border-dashed border-slate-200">
                  <Pill className="h-8 w-8 text-slate-300 mx-auto mb-2" />
                  <p className="text-xs text-slate-600 font-medium">No templates found</p>
                  <Button
                    variant="link"
                    size="sm"
                    onClick={() => setActiveTab('create')}
                    className="text-teal-600 text-xs mt-1"
                  >
                    Create the first template
                  </Button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {filteredTemplates.map((t) => (
                    <div
                      key={t.id}
                      className="p-4 rounded-xl border border-slate-200 hover:border-teal-400 bg-white shadow-sm flex flex-col justify-between transition-all"
                    >
                      <div className="space-y-2">
                        <div className="flex items-start justify-between gap-2">
                          <h4 className="font-bold text-xs text-slate-900">{t.name}</h4>
                          <Badge variant="outline" className="text-[10px] bg-teal-50 text-teal-700 border-teal-200 shrink-0">
                            {t.category || 'General'}
                          </Badge>
                        </div>
                        {t.description && (
                          <p className="text-[11px] text-slate-500 line-clamp-2">{t.description}</p>
                        )}
                        <div className="bg-slate-50 p-2 rounded text-[11px] space-y-1">
                          <span className="font-semibold text-slate-700 text-[10px] uppercase">
                            Includes ({t.items?.length || 0} meds):
                          </span>
                          {(t.items || []).slice(0, 3).map((it, idx) => (
                            <p key={idx} className="text-slate-600 truncate">
                              • <strong>{it.medicineName}</strong> ({it.amount || it.dosage} - {it.frequency})
                            </p>
                          ))}
                          {t.items?.length > 3 && (
                            <p className="text-[10px] text-slate-400">+{t.items.length - 3} more...</p>
                          )}
                        </div>
                      </div>

                      <div className="pt-3 mt-3 border-t border-slate-100 flex items-center justify-end">
                        <Button
                          size="sm"
                          onClick={() => handleApply(t)}
                          className="bg-teal-600 hover:bg-teal-700 text-white text-xs h-7 px-3 font-semibold shadow-sm"
                        >
                          <Check className="h-3.5 w-3.5 mr-1" /> Apply to Encounter
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold text-slate-700">
                    Template Name <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    placeholder="e.g. Acute Pharyngitis / Viral Fever"
                    value={templateName}
                    onChange={(e) => setTemplateName(e.target.value)}
                    className="text-xs"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold text-slate-700">Category</Label>
                  <Input
                    placeholder="e.g. General OPD, Pediatrics, Cardiology"
                    value={templateCategory}
                    onChange={(e) => setTemplateCategory(e.target.value)}
                    className="text-xs"
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-slate-700">Clinical Protocol Notes</Label>
                <Textarea
                  placeholder="Clinical guidance or advice associated with this template..."
                  value={templateDescription}
                  onChange={(e) => setTemplateDescription(e.target.value)}
                  rows={2}
                  className="text-xs"
                />
              </div>

              <div className="space-y-3 pt-2">
                <div className="flex items-center justify-between">
                  <Label className="text-xs font-bold text-slate-800 flex items-center gap-1.5">
                    <Pill className="h-3.5 w-3.5 text-teal-600" />
                    Medicines in Template ({items.length})
                  </Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleAddItem}
                    className="text-xs border-teal-300 text-teal-700 h-7"
                  >
                    <Plus className="h-3 w-3 mr-1" /> Add Medication
                  </Button>
                </div>

                <div className="space-y-2.5 max-h-[260px] overflow-y-auto pr-1">
                  {items.map((it, idx) => (
                    <div
                      key={idx}
                      className="p-2.5 bg-slate-50 border border-slate-200 rounded-lg space-y-2"
                    >
                      <div className="grid grid-cols-1 sm:grid-cols-12 gap-2 items-center">
                        <div className="sm:col-span-4">
                          <Input
                            placeholder="Medicine Name (e.g. Dolo 650)"
                            value={it.medicineName}
                            onChange={(e) => handleItemChange(idx, 'medicineName', e.target.value)}
                            className="text-xs bg-white"
                          />
                        </div>
                        <div className="sm:col-span-2">
                          <Input
                            placeholder="Amount (1 tab)"
                            value={it.amount}
                            onChange={(e) => handleItemChange(idx, 'amount', e.target.value)}
                            className="text-xs bg-white"
                          />
                        </div>
                        <div className="sm:col-span-2">
                          <Input
                            placeholder="Qty (10)"
                            type="number"
                            value={it.quantity}
                            onChange={(e) => handleItemChange(idx, 'quantity', e.target.value)}
                            className="text-xs bg-white"
                          />
                        </div>
                        <div className="sm:col-span-3">
                          <Input
                            placeholder="Frequency (TDS)"
                            value={it.frequency}
                            onChange={(e) => handleItemChange(idx, 'frequency', e.target.value)}
                            className="text-xs bg-white"
                          />
                        </div>
                        <div className="sm:col-span-1 flex justify-end">
                          {items.length > 1 && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              onClick={() => handleRemoveItem(idx)}
                              className="h-7 w-7 text-red-500 hover:bg-red-50"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          )}
                        </div>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-12 gap-2">
                        <div className="sm:col-span-3">
                          <Input
                            placeholder="Duration (5 days)"
                            value={it.duration}
                            onChange={(e) => handleItemChange(idx, 'duration', e.target.value)}
                            className="text-xs bg-white"
                          />
                        </div>
                        <div className="sm:col-span-9">
                          <Input
                            placeholder="Instructions (e.g. Take after meals)"
                            value={it.notes}
                            onChange={(e) => handleItemChange(idx, 'notes', e.target.value)}
                            className="text-xs bg-white"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="p-4 bg-slate-50 border-t border-slate-200 flex items-center justify-between">
          <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)} className="text-xs">
            Close
          </Button>
          {activeTab === 'create' && (
            <Button
              size="sm"
              onClick={handleSaveTemplate}
              className="bg-teal-600 hover:bg-teal-700 text-white text-xs font-bold"
            >
              <Sparkles className="h-3.5 w-3.5 mr-1" /> Save Template
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PrescriptionTemplateModal;

